import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserPlus, 
  Lock, 
  Pencil, 
  Info, 
  Building2, 
  ChevronDown, 
  ChevronUp,
  Search,
  Users,
  Clock,
  Mail,
  Phone,
  Trash2,
  X,
  Check,
  Save,
  Loader2,
  RefreshCw,
  Building,
  ChevronRight,
  ChevronLeft,
  Plus,
  User
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { intelligentSearch } from '../utils/searchUtils';
import Modal from '../components/Modal';
import './Staff.css';

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [scheduleData, setScheduleData] = useState({});
  const [scheduleTab, setScheduleTab] = useState('semanal');
  const [newSpecificDate, setNewSpecificDate] = useState('');
  const [newOffDate, setNewOffDate] = useState('');
  const [newOffDateEnd, setNewOffDateEnd] = useState('');
  const [saving, setSaving] = useState(false);

  // Active user to check permissions
  const activeUser = JSON.parse(localStorage.getItem('gdt_active_user') || '{}');
  const isSecretaria = activeUser.role === 'secretaria';

  // New modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedStaffForEdit, setSelectedStaffForEdit] = useState(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedStaffForInfo, setSelectedStaffForInfo] = useState(null);
  const [isBranchesModalOpen, setIsBranchesModalOpen] = useState(false);
  const [selectedStaffForBranches, setSelectedStaffForBranches] = useState(null);
  const [branchesList, setBranchesList] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [savingBranches, setSavingBranches] = useState(false);

  // User credentials management states
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [selectedStaffForUsers, setSelectedStaffForUsers] = useState(null);
  const [tempUsersList, setTempUsersList] = useState([]);
  const [savingUsers, setSavingUsers] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');

  // Services list for checkboxes
  const [servicesList, setServicesList] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);

  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('professionals')
        .select('*')
        .order('full_name');
      if (error) throw error;
      setStaff(data || []);
    } catch (err) {
      addNotification('Error al cargar personal', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase.from('services').select('*').order('name');
      if (error) throw error;
      setServicesList(data || []);
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  };

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchStaff();
      fetchServices();
    });
  }, []);

  const handleAddStaff = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newMember = {
      full_name: formData.get('full_name'),
      type: formData.get('type'),
      specialty: formData.get('specialty'),
      registration: formData.get('registration'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      is_hidden: false,
      services: selectedServices
    };

    try {
      const { error } = await supabase.from('professionals').insert([newMember]);
      if (error) throw error;
      addNotification('Integrante agregado con éxito', 'success');
      setIsNewModalOpen(false);
      fetchStaff();
    } catch (err) {
      addNotification('Error al agregar: ' + err.message, 'error');
    }
  };

  const handleOpenEdit = async (member) => {
    setSelectedStaffForEdit(member);
    setSelectedServices(member.services || []);
    // Load branches for the sede selector
    if (branchesList.length === 0) {
      try {
        const { data } = await supabase.from('branches').select('*').order('name');
        setBranchesList(data || []);
      } catch (e) { console.error(e); }
    }
    setIsEditModalOpen(true);
  };

  const handleEditStaff = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const branchIdVal = formData.get('branch_id');
    const updatedMember = {
      full_name: formData.get('full_name'),
      type: formData.get('type'),
      specialty: formData.get('specialty'),
      registration: formData.get('registration'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      services: selectedServices,
      branch_id: branchIdVal ? parseInt(branchIdVal) : null,
      wa_template: formData.get('wa_template') || null
    };

    try {
      const { error } = await supabase
        .from('professionals')
        .update(updatedMember)
        .eq('id', selectedStaffForEdit.id);
      if (error) throw error;
      addNotification('Integrante modificado con éxito', 'success');
      setIsEditModalOpen(false);
      fetchStaff();
    } catch (err) {
      addNotification('Error al modificar integrante: ' + err.message, 'error');
    }
  };

  const handleOpenInfo = (member) => {
    setSelectedStaffForInfo(member);
    setIsInfoModalOpen(true);
  };

  const handleOpenBranches = async (member) => {
    setSelectedStaffForBranches(member);
    try {
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .order('name');
      if (error) throw error;
      setBranchesList(data || []);
      
      const currentBranches = member.working_hours?.branches || [];
      setSelectedBranches(currentBranches);
      setIsBranchesModalOpen(true);
    } catch (err) {
      addNotification('Error al cargar sucursales: ' + err.message, 'error');
    }
  };

  const handleSaveBranches = async () => {
    setSavingBranches(true);
    try {
      const updatedWorkingHours = {
        ...(selectedStaffForBranches.working_hours || {}),
        branches: selectedBranches
      };

      const { error } = await supabase
        .from('professionals')
        .update({ working_hours: updatedWorkingHours })
        .eq('id', selectedStaffForBranches.id);

      if (error) throw error;
      addNotification('Sucursales asignadas con éxito', 'success');
      setIsBranchesModalOpen(false);
      fetchStaff();
    } catch (err) {
      addNotification('Error al asignar sucursales: ' + err.message, 'error');
    } finally {
      setSavingBranches(false);
    }
  };

  const handleToggleBranchCheckbox = (branchId) => {
    if (selectedBranches.includes(branchId)) {
      setSelectedBranches(selectedBranches.filter(id => id !== branchId));
    } else {
      setSelectedBranches([...selectedBranches, branchId]);
    }
  };

  const handleOpenSchedule = (member) => {
    setSelectedStaff(member);
    setScheduleData(member.working_hours || {
      mon: { enabled: true, start: '08:00', end: '13:00', has_shift2: true, start2: '14:00', end2: '20:00' },
      tue: { enabled: true, start: '08:00', end: '13:00', has_shift2: true, start2: '14:00', end2: '20:00' },
      wed: { enabled: true, start: '08:00', end: '13:00', has_shift2: true, start2: '14:00', end2: '20:00' },
      thu: { enabled: true, start: '08:00', end: '13:00', has_shift2: true, start2: '14:00', end2: '20:00' },
      fri: { enabled: true, start: '08:00', end: '13:00', has_shift2: true, start2: '14:00', end2: '20:00' },
      sat: { enabled: false, start: '08:00', end: '13:00', has_shift2: false, start2: '', end2: '' },
      sun: { enabled: false },
      slot_duration: 30,
      max_overbooking: 0,
      specific_dates: {},
      off_dates: []
    });
    setIsScheduleModalOpen(true);
  };

  const handleSaveSchedule = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('professionals')
        .update({ working_hours: scheduleData })
        .eq('id', selectedStaff.id);
      
      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }
      addNotification('Horarios actualizados correctamente', 'success');
      setIsScheduleModalOpen(false);
      fetchStaff();
    } catch (err) {
      console.error('Error in handleSaveSchedule:', err);
      addNotification('Error al guardar horarios: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenUsers = (member) => {
    setSelectedStaffForUsers(member);
    setTempUsersList(member.users || []);
    setNewDisplayName('');
    setNewDisplayName('');
    setNewUsername('');
    setNewPassword('');
    setNewUserRole('user');
    setIsUsersModalOpen(true);
  };

  const handleSaveUsers = async () => {
    setSavingUsers(true);
    try {
      const { error } = await supabase
        .from('professionals')
        .update({ users: tempUsersList })
        .eq('id', selectedStaffForUsers.id);

      if (error) throw error;
      addNotification('Usuarios actualizados con éxito', 'success');
      setIsUsersModalOpen(false);
      fetchStaff();
    } catch (err) {
      addNotification('Error al guardar usuarios: ' + err.message, 'error');
    } finally {
      setSavingUsers(false);
    }
  };

  const handleAddUser = (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword.trim()) {
      addNotification('Usuario y contraseña son requeridos', 'warning');
      return;
    }
    if (tempUsersList.some(u => u.username.toLowerCase() === newUsername.toLowerCase().trim())) {
      addNotification('El nombre de usuario ya existe', 'warning');
      return;
    }
    const newUser = {
      display_name: newDisplayName.trim(),
      username: newUsername.trim(),
      password: newPassword.trim(),
      role: newUserRole
    };
    setTempUsersList([...tempUsersList, newUser]);
    setNewDisplayName('');
    setNewUsername('');
    setNewPassword('');
    setNewUserRole('user');
    addNotification('Usuario agregado a la lista', 'success');
  };

  const handleUpdateUserField = (idx, field, value) => {
    const updatedList = [...tempUsersList];
    updatedList[idx] = {
      ...updatedList[idx],
      [field]: value
    };
    setTempUsersList(updatedList);
  };

  const handleDeleteUser = (idx) => {
    const updatedList = tempUsersList.filter((_, i) => i !== idx);
    setTempUsersList(updatedList);
    addNotification('Usuario removido de la lista', 'info');
  };

  const filteredStaff = staff.filter(s => 
    intelligentSearch(searchTerm, s.full_name, s.specialty)
  );

  const days = [
    { id: 'mon', label: 'Lunes' },
    { id: 'tue', label: 'Martes' },
    { id: 'wed', label: 'Miércoles' },
    { id: 'thu', label: 'Jueves' },
    { id: 'fri', label: 'Viernes' },
    { id: 'sat', label: 'Sábado' },
    { id: 'sun', label: 'Domingo' }
  ];

  const buildHierarchy = (staffList) => {
    let parents = [];
    let children = [];
    
    staffList.forEach(s => {
       if (s.full_name?.trim().endsWith('.')) {
         parents.push({...s, childrenList: []});
       } else {
         children.push(s);
       }
    });

    let unattached = [];
    children.forEach(c => {
       if (!c.specialty) {
         unattached.push(c);
         return;
       }
       const specPrefix = c.specialty.trim().toLowerCase().substring(0, 5);
       if (specPrefix.length >= 3) {
         const parent = parents.find(p => p.full_name.toLowerCase().startsWith(specPrefix));
         if (parent) {
           parent.childrenList.push(c);
           return;
         }
       }
       unattached.push(c);
    });

    let topLevel = [...parents, ...unattached].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
    
    let result = [];
    topLevel.forEach(item => {
      result.push({...item, isChild: false});
      if (item.childrenList) {
        item.childrenList.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
        item.childrenList.forEach(child => {
          result.push({...child, isChild: true});
        });
      }
    });
    return result;
  };

  return (
    <div className="staff-page">
      <div className="staff-header-v3">
        <div className="staff-title-v3">
          <Users size={32} className="title-icon-v3" />
          <h1>Staff del Consultorio</h1>
        </div>
        <div className="staff-header-actions-v3">
          {!isSecretaria && (
            <button className="btn-staff-top blue" onClick={() => { setSelectedServices([]); setIsNewModalOpen(true); }}>
              NUEVA PERSONA DEL STAFF <Plus size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="staff-filters card glass-panel">
        <div className="search-bar-staff">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Buscar por nombre o especialidad..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn-refresh-staff" onClick={fetchStaff}><RefreshCw size={18} /></button>
      </div>

      <div className="staff-table-v3-wrapper card">
        <table className="staff-table-v3">
          <thead>
            <tr>
              <th className="sortable">Nombre <ChevronUp size={12} /></th>
              <th>Tipo</th>
              <th>Especialidad</th>
              <th>Matricula</th>
              <th className="text-center">Horarios</th>
              <th className="text-center">Modificar</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="text-center">Cargando staff...</td></tr>
            ) : filteredStaff.length === 0 ? (
              <tr><td colSpan="6" className="text-center">No hay registros.</td></tr>
            ) : buildHierarchy(filteredStaff).map(member => (
              <tr key={member.id} className={member.isChild ? 'staff-child-row' : ''}>
                <td className="member-name-cell">
                  {member.isChild && <div className="child-indent-line"></div>}
                  {member.full_name || member.name}
                </td>
                <td>{member.type}</td>
                <td>{member.specialty || ''}</td>
                <td>{member.registration || ''}</td>
                <td className="text-center">
                  {!isSecretaria ? (
                    <button className="btn-change-schedule" onClick={() => handleOpenSchedule(member)}>
                      CONFIGURAR
                    </button>
                  ) : (
                    <span style={{color: '#999', fontSize: '12px'}}>No permitido</span>
                  )}
                </td>
                <td className="text-center">
                  <div className="staff-actions-row">
                    <button className="btn-staff-action edit" onClick={() => handleOpenEdit(member)} title="Editar"><Pencil size={14} /></button>
                    <button className="btn-staff-action info" onClick={() => handleOpenInfo(member)} title="Ver ficha"><Info size={14} /></button>
                    <button className="btn-staff-action users" onClick={() => handleOpenUsers(member)} title="Gestionar Accesos"><Lock size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isNewModalOpen} onClose={() => setIsNewModalOpen(false)} title="Agregar Integrante al Staff">
        <form onSubmit={handleAddStaff} className="staff-form-premium">
          <div className="form-group-p">
            <label>Nombre Completo</label>
            <input name="full_name" type="text" required placeholder="Ej: Dr. Juan Carlos Rossi" />
          </div>
          <div className="form-grid-p">
            <div className="form-group-p">
              <label>Tipo de Staff</label>
              <select name="type">
                <option value="Profesional">Profesional Médico</option>
                <option value="Secretaria/o">Secretaría / Recepción</option>
              </select>
            </div>
            <div className="form-group-p">
              <label>Especialidad</label>
              <input name="specialty" type="text" placeholder="Ej: Pediatría" />
            </div>
          </div>
          
          <div className="form-group-p" style={{ marginTop: '1rem' }}>
            <label>Estudios y Prácticas que realiza</label>
            <div className="services-checkbox-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto', padding: '0.5rem', background: 'rgba(0,0,0,0.1)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
              {servicesList.map(srv => (
                <label key={srv.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer', color: 'var(--text-main)' }}>
                  <input 
                    type="checkbox" 
                    checked={selectedServices.includes(srv.id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedServices([...selectedServices, srv.id]);
                      else setSelectedServices(selectedServices.filter(id => id !== srv.id));
                    }}
                  />
                  {srv.name}
                </label>
              ))}
              {servicesList.length === 0 && <span style={{ fontSize: '0.8rem', color: '#888' }}>No hay estudios cargados en el sistema.</span>}
            </div>
          </div>

          <div className="form-footer-p">
            <button type="button" className="btn-cancel-modal" onClick={() => setIsNewModalOpen(false)}>CANCELAR</button>
            <button type="submit" className="btn-save-p">GUARDAR EN STAFF</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} title={`Configurar Horarios: ${selectedStaff?.full_name || selectedStaff?.name || ''}`}>
        <div className="schedule-config-modal">
          <div className="settings-tabs" style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <button 
              className={`tab-btn ${scheduleTab === 'semanal' ? 'active' : ''}`} 
              onClick={() => setScheduleTab('semanal')}
              style={{ padding: '0.5rem 1rem', background: 'none', border: 'none', color: scheduleTab === 'semanal' ? 'var(--primary-cyan)' : 'var(--text-main)', borderBottom: scheduleTab === 'semanal' ? '2px solid var(--primary-cyan)' : 'none', cursor: 'pointer', fontWeight: scheduleTab === 'semanal' ? 'bold' : 'normal' }}
            >
              Días Regulares
            </button>
            <button 
              className={`tab-btn ${scheduleTab === 'especificas' ? 'active' : ''}`} 
              onClick={() => setScheduleTab('especificas')}
              style={{ padding: '0.5rem 1rem', background: 'none', border: 'none', color: scheduleTab === 'especificas' ? 'var(--primary-cyan)' : 'var(--text-main)', borderBottom: scheduleTab === 'especificas' ? '2px solid var(--primary-cyan)' : 'none', cursor: 'pointer', fontWeight: scheduleTab === 'especificas' ? 'bold' : 'normal' }}
            >
              Fechas Específicas
            </button>
            <button 
              className={`tab-btn ${scheduleTab === 'vacaciones' ? 'active' : ''}`} 
              onClick={() => setScheduleTab('vacaciones')}
              style={{ padding: '0.5rem 1rem', background: 'none', border: 'none', color: scheduleTab === 'vacaciones' ? 'var(--primary-cyan)' : 'var(--text-main)', borderBottom: scheduleTab === 'vacaciones' ? '2px solid var(--primary-cyan)' : 'none', cursor: 'pointer', fontWeight: scheduleTab === 'vacaciones' ? 'bold' : 'normal' }}
            >
              Vacaciones / Días Libres
            </button>
          </div>

          {scheduleTab === 'semanal' && (
            <>
              <div className="schedule-global-settings" style={{ marginBottom: '1.5rem', background: 'rgba(255,255,255,0.05)', padding: '1rem', borderRadius: '12px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--text-main)' }}>
                  Cadencia / Duración de Turnos (minutos)
                </label>
                <select 
                  value={scheduleData.slot_duration || 30}
                  onChange={(e) => setScheduleData({ ...scheduleData, slot_duration: parseInt(e.target.value, 10) })}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'var(--bg-lighter)', color: 'var(--text-main)' }}
                >
              <option value={5}>Cada 5 minutos</option>
              <option value={10}>Cada 10 minutos</option>
              <option value={15}>Cada 15 minutos</option>
              <option value={20}>Cada 20 minutos</option>
              <option value={30}>Cada 30 minutos</option>
              <option value={40}>Cada 40 minutos</option>
              <option value={60}>Cada 1 hora</option>
            </select>
          </div>
          <div className="schedule-global-settings" style={{ marginBottom: '1.5rem', background: 'rgba(255,255,255,0.05)', padding: '1rem', borderRadius: '12px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--text-main)' }}>
              Permitir Sobre Turnos (por bloque horario)
            </label>
            <select 
              value={scheduleData.max_overbooking || 0}
              onChange={(e) => setScheduleData({ ...scheduleData, max_overbooking: parseInt(e.target.value, 10) })}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'var(--bg-lighter)', color: 'var(--text-main)' }}
            >
              <option value={0}>Sin sobre turnos</option>
              <option value={1}>1 sobre turno</option>
              <option value={2}>2 sobre turnos</option>
              <option value={3}>3 sobre turnos</option>
              <option value={4}>4 sobre turnos</option>
              <option value={5}>5 sobre turnos</option>
            </select>
          </div>
          <div className="schedule-list">
            {days.map(day => (
              <div key={day.id} className={`schedule-day-row ${scheduleData[day.id]?.enabled ? 'enabled' : 'disabled'}`}>
                <div className="day-info">
                  <div className="day-checkbox-wrap">
                    <input 
                      type="checkbox" 
                      checked={scheduleData[day.id]?.enabled} 
                      onChange={(e) => setScheduleData({
                        ...scheduleData,
                        [day.id]: { ...scheduleData[day.id], enabled: e.target.checked }
                      })}
                    />
                    <label>{day.label}</label>
                  </div>
                </div>
                {scheduleData[day.id]?.enabled && (
                  <div className="time-inputs" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="time-group">
                        <label>Turno 1: Desde</label>
                        <input 
                          type="time" 
                          value={scheduleData[day.id]?.start || '08:00'} 
                          onChange={(e) => setScheduleData({
                            ...scheduleData,
                            [day.id]: { ...scheduleData[day.id], start: e.target.value }
                          })}
                        />
                      </div>
                      <div className="time-sep">/</div>
                      <div className="time-group">
                        <label>Hasta</label>
                        <input 
                          type="time" 
                          value={scheduleData[day.id]?.end || '13:00'} 
                          onChange={(e) => setScheduleData({
                            ...scheduleData,
                            [day.id]: { ...scheduleData[day.id], end: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem', color: 'var(--text-main)', marginRight: '10px' }}>
                        <input 
                          type="checkbox" 
                          checked={scheduleData[day.id]?.has_shift2 || false}
                          onChange={(e) => setScheduleData({
                            ...scheduleData,
                            [day.id]: { ...scheduleData[day.id], has_shift2: e.target.checked }
                          })}
                        /> Turno Tarde
                      </label>
                      {scheduleData[day.id]?.has_shift2 && (
                        <>
                          <div className="time-group">
                            <label>Turno 2: Desde</label>
                            <input 
                              type="time" 
                              value={scheduleData[day.id]?.start2 || '14:00'} 
                              onChange={(e) => setScheduleData({
                                ...scheduleData,
                                [day.id]: { ...scheduleData[day.id], start2: e.target.value }
                              })}
                            />
                          </div>
                          <div className="time-sep">/</div>
                          <div className="time-group">
                            <label>Hasta</label>
                            <input 
                              type="time" 
                              value={scheduleData[day.id]?.end2 || '20:00'} 
                              onChange={(e) => setScheduleData({
                                ...scheduleData,
                                [day.id]: { ...scheduleData[day.id], end2: e.target.value }
                              })}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {!scheduleData[day.id]?.enabled && <div className="closed-msg">NO ATIENDE</div>}
              </div>
            ))}
          </div>
          </>
          )}

          {scheduleTab === 'especificas' && (
            <div style={{ padding: '1rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>
                Útil para médicos que atienden una vez al mes o esporádicamente. Las fechas específicas ignoran la configuración regular (semanal).
              </p>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
                <input 
                  type="date" 
                  value={newSpecificDate} 
                  onChange={(e) => setNewSpecificDate(e.target.value)} 
                  style={{ width: '200px' }}
                />
                <button className="btn-primary" onClick={() => {
                  if (newSpecificDate && !(scheduleData.specific_dates || {})[newSpecificDate]) {
                    setScheduleData({
                      ...scheduleData,
                      specific_dates: {
                        ...(scheduleData.specific_dates || {}),
                        [newSpecificDate]: { start: '08:00', end: '13:00', has_shift2: false, start2: '14:00', end2: '20:00' }
                      }
                    });
                    setNewSpecificDate('');
                  }
                }}>AÑADIR FECHA</button>
              </div>

              <div className="specific-dates-list" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {Object.keys(scheduleData.specific_dates || {}).sort().map(date => {
                  const s = scheduleData.specific_dates[date];
                  return (
                    <div key={date} style={{ background: 'rgba(255,255,255,0.05)', padding: '1rem', borderRadius: '8px', borderLeft: '3px solid var(--primary-cyan)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontWeight: 'bold' }}>{date}</span>
                        <button style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer' }} onClick={() => {
                          const newSD = { ...scheduleData.specific_dates };
                          delete newSD[date];
                          setScheduleData({ ...scheduleData, specific_dates: newSD });
                        }}>Eliminar</button>
                      </div>
                      
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                        <div className="time-group">
                          <label>Turno 1: Desde</label>
                          <input type="time" value={s.start} onChange={(e) => setScheduleData({ ...scheduleData, specific_dates: { ...scheduleData.specific_dates, [date]: { ...s, start: e.target.value } } })} />
                        </div>
                        <div className="time-group">
                          <label>Hasta</label>
                          <input type="time" value={s.end} onChange={(e) => setScheduleData({ ...scheduleData, specific_dates: { ...scheduleData.specific_dates, [date]: { ...s, end: e.target.value } } })} />
                        </div>
                      </div>

                      <div style={{ marginTop: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem', color: 'var(--text-main)' }}>
                          <input type="checkbox" checked={s.has_shift2} onChange={(e) => setScheduleData({ ...scheduleData, specific_dates: { ...scheduleData.specific_dates, [date]: { ...s, has_shift2: e.target.checked } } })} />
                          Turno Tarde
                        </label>
                        {s.has_shift2 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '10px' }}>
                            <div className="time-group">
                              <label>Turno 2: Desde</label>
                              <input type="time" value={s.start2} onChange={(e) => setScheduleData({ ...scheduleData, specific_dates: { ...scheduleData.specific_dates, [date]: { ...s, start2: e.target.value } } })} />
                            </div>
                            <div className="time-group">
                              <label>Hasta</label>
                              <input type="time" value={s.end2} onChange={(e) => setScheduleData({ ...scheduleData, specific_dates: { ...scheduleData.specific_dates, [date]: { ...s, end2: e.target.value } } })} />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {Object.keys(scheduleData.specific_dates || {}).length === 0 && (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>No hay fechas específicas asignadas.</p>
                )}
              </div>
            </div>
          )}

          {scheduleTab === 'vacaciones' && (
            <div style={{ padding: '1rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>
                Agrega fechas en las que el profesional NO atenderá. La agenda se mostrará bloqueada para esas fechas.
              </p>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <label style={{display:'block', fontSize:'12px', marginBottom:'4px'}}>Desde</label>
                  <input 
                    type="date" 
                    value={newOffDate} 
                    onChange={(e) => setNewOffDate(e.target.value)} 
                    style={{ width: '180px' }}
                  />
                </div>
                <div>
                  <label style={{display:'block', fontSize:'12px', marginBottom:'4px'}}>Hasta (Opcional)</label>
                  <input 
                    type="date" 
                    value={newOffDateEnd} 
                    onChange={(e) => setNewOffDateEnd(e.target.value)} 
                    style={{ width: '180px' }}
                    min={newOffDate}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <button className="btn-primary" onClick={() => {
                    if (newOffDate) {
                      let datesToAdd = [];
                      if (newOffDateEnd && newOffDateEnd >= newOffDate) {
                        let current = new Date(newOffDate);
                        const end = new Date(newOffDateEnd);
                        // fix timezone issue by adding 12 hours
                        current.setHours(12);
                        end.setHours(12);
                        while (current <= end) {
                          datesToAdd.push(current.toISOString().split('T')[0]);
                          current.setDate(current.getDate() + 1);
                        }
                      } else {
                        datesToAdd.push(newOffDate);
                      }
                      
                      const currentDates = scheduleData.off_dates || [];
                      const uniqueNewDates = datesToAdd.filter(d => !currentDates.includes(d));
                      
                      if (uniqueNewDates.length > 0) {
                        setScheduleData({ ...scheduleData, off_dates: [...currentDates, ...uniqueNewDates].sort() });
                      }
                      setNewOffDate('');
                      setNewOffDateEnd('');
                    }
                  }}>AÑADIR DÍAS LIBRES</button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {(scheduleData.off_dates || []).sort().map(date => (
                  <div key={date} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.05)', padding: '0.5rem 1rem', borderRadius: '20px' }}>
                    <span>{date}</span>
                    <span onClick={() => {
                      setScheduleData({ ...scheduleData, off_dates: scheduleData.off_dates.filter(d => d !== date) });
                    }} style={{ cursor: 'pointer', color: 'var(--danger)' }}>&times;</span>
                  </div>
                ))}
                {(scheduleData.off_dates || []).length === 0 && (
                  <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)' }}>No hay días libres programados.</span>
                )}
              </div>
            </div>
          )}

          <div className="form-footer-p" style={{ marginTop: '2rem' }}>
            <button className="btn-cancel-modal" onClick={() => setIsScheduleModalOpen(false)}>CANCELAR</button>
            <button className="btn-save-p" onClick={handleSaveSchedule} disabled={saving}>
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              GUARDAR HORARIOS
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Modificar Integrante del Staff">
        {selectedStaffForEdit && (
          <form onSubmit={handleEditStaff} className="staff-form-premium">
            <div className="form-group-p">
              <label>Nombre Completo</label>
              <input name="full_name" type="text" required defaultValue={selectedStaffForEdit.full_name} placeholder="Dr. Juan Carlos Rossi" />
            </div>
            <div className="form-grid-p">
              <div className="form-group-p">
                <label>Tipo de Staff</label>
                <select name="type" defaultValue={selectedStaffForEdit.type}>
                  <option value="Profesional">Profesional Médico</option>
                  <option value="Secretaria/o">Secretaría / Recepción</option>
                  <option value="Administrador">Administrador</option>
                </select>
              </div>
              <div className="form-group-p">
                <label>Especialidad</label>
                <input name="specialty" type="text" defaultValue={selectedStaffForEdit.specialty || ''} placeholder="Ej: Pediatría" />
              </div>
            </div>
            <div className="form-grid-p">
              <div className="form-group-p">
                <label>Matrícula / Registro</label>
                <input name="registration" type="text" defaultValue={selectedStaffForEdit.registration || ''} placeholder="Ej: MN 123456" />
              </div>
              <div className="form-group-p">
                <label>Email Laboral</label>
                <input name="email" type="email" defaultValue={selectedStaffForEdit.email || ''} placeholder="profesional@gdt.com" />
              </div>
            </div>
            <div className="form-grid-p">
              <div className="form-group-p">
                <label>Teléfono de Contacto</label>
                <input name="phone" type="tel" defaultValue={selectedStaffForEdit.phone || ''} placeholder="Ej: +54 9 11 5555-5555" />
              </div>
              <div className="form-group-p"></div>
            </div>

            <div className="form-group-p" style={{ marginTop: '1rem' }}>
              <label>Estudios y Prácticas que realiza</label>
              <div className="services-checkbox-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto', padding: '0.5rem', background: 'rgba(0,0,0,0.1)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                {servicesList.map(srv => (
                  <label key={srv.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer', color: 'var(--text-main)' }}>
                    <input 
                      type="checkbox" 
                      checked={selectedServices.includes(srv.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedServices([...selectedServices, srv.id]);
                        else setSelectedServices(selectedServices.filter(id => id !== srv.id));
                      }}
                    />
                    {srv.name}
                  </label>
                ))}
                {servicesList.length === 0 && <span style={{ fontSize: '0.8rem', color: '#888' }}>No hay estudios cargados en el sistema.</span>}
              </div>
            </div>

            {/* WhatsApp Sede & Template per Professional */}
            <div className="form-group-p" style={{ marginTop: '1.25rem', borderTop: '1px solid rgba(0,229,255,0.15)', paddingTop: '1.25rem' }}>
              <label style={{ color: '#00e5ff', fontWeight: 'bold' }}>📱 Sede por defecto para WhatsApp</label>
              <select name="branch_id" defaultValue={selectedStaffForEdit.branch_id || ''} style={{ marginTop: '0.5rem' }}>
                <option value="">— Usar datos globales del sistema —</option>
                {branchesList.map(b => (
                  <option key={b.id} value={b.id}>{b.name} — {b.address}</option>
                ))}
              </select>
              <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                Cuando se envíe un recordatorio para este profesional, el campo {'{sede}'}, {'{sede_direccion}'}, {'{sede_telefono}'} y {'[[WHATSAPP]]'} usarán los datos de esta sede.
              </p>
            </div>

            <div className="form-group-p" style={{ marginTop: '1rem' }}>
              <label style={{ color: '#00e5ff', fontWeight: 'bold' }}>💬 Plantilla de WhatsApp personalizada (Opcional)</label>
              <textarea 
                name="wa_template" 
                rows="6" 
                defaultValue={selectedStaffForEdit.wa_template || ''} 
                placeholder="Si lo dejas vacío, se usará la plantilla global del sistema. Usa las mismas variables: {paciente}, {fecha}, {hora}, {profesional}, {sede_direccion}, {link_confirmacion}, etc."
                style={{ marginTop: '0.5rem', resize: 'vertical', fontFamily: 'monospace', fontSize: '0.82rem' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                Esta plantilla SOLO aplica para los turnos de este profesional/área. Si está vacío, se usa la plantilla global.
              </p>
            </div>

            <div className="form-footer-p">
              <button type="button" className="btn-cancel-modal" onClick={() => setIsEditModalOpen(false)}>CANCELAR</button>
              <button type="submit" className="btn-save-p">GUARDAR CAMBIOS</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} title="Ficha del Staff">
        {selectedStaffForInfo && (
          <div className="staff-profile-card">
            <div className="profile-header-premium">
              <div className="profile-avatar-circle">
                {(selectedStaffForInfo.full_name || selectedStaffForInfo.name || 'S')[0]}
              </div>
              <div className="profile-name-section">
                <h3>{selectedStaffForInfo.full_name || selectedStaffForInfo.name || 'Sin Nombre'}</h3>
                <span className="profile-role-tag">{selectedStaffForInfo.type}</span>
              </div>
            </div>

            <div className="profile-details-grid">
              {selectedStaffForInfo.type === 'Profesional' && (
                <>
                  <div className="profile-info-item">
                    <span className="label">Especialidad</span>
                    <span className="val">{selectedStaffForInfo.specialty || 'General'}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="label">Matrícula / Registro</span>
                    <span className="val">{selectedStaffForInfo.registration || '---'}</span>
                  </div>
                </>
              )}
              <div className="profile-info-item">
                <span className="label">Email Laboral</span>
                <span className="val">{selectedStaffForInfo.email || '---'}</span>
              </div>
              <div className="profile-info-item">
                <span className="label">Teléfono</span>
                <span className="val">{selectedStaffForInfo.phone || '---'}</span>
              </div>
            </div>

            {selectedStaffForInfo.type === 'Profesional' && (
              <>
                <div className="profile-section-title">Sucursales de Atención</div>
                <div className="profile-branches-list">
                  {selectedStaffForInfo.working_hours?.branches && selectedStaffForInfo.working_hours.branches.length > 0 ? (
                    selectedStaffForInfo.working_hours.branches.map(bId => {
                      const branchNames = { 1: 'Belgrano', 2: 'Nuñez', 3: 'Ramos Mejía', 4: 'Caballito' };
                      return (
                        <span key={bId} className="profile-branch-tag">
                          <Building size={12} /> {branchNames[bId] || `Sede #${bId}`}
                        </span>
                      );
                    })
                  ) : (
                    <span className="no-branches-text">No tiene sucursales asignadas.</span>
                  )}
                </div>

                <div className="profile-section-title">Horario de Atención</div>
                <div className="profile-schedule-compact">
                  {Object.entries(selectedStaffForInfo.working_hours || {}).map(([dayKey, dayData]) => {
                    if (dayKey === 'branches') return null;
                    const dayNames = { mon: 'Lunes', tue: 'Martes', wed: 'Miércoles', thu: 'Jueves', fri: 'Viernes', sat: 'Sábado', sun: 'Domingo' };
                    return (
                      <div key={dayKey} className="profile-schedule-row">
                        <span className="day-name">{dayNames[dayKey] || dayKey}:</span>
                        <span className={`day-hours ${dayData?.enabled ? 'enabled' : 'disabled'}`}>
                          {dayData?.enabled ? `${dayData.start} - ${dayData.end} hs` : 'No atiende'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            
            <div className="form-footer-p" style={{ marginTop: '2rem' }}>
              <button className="btn-save-p" onClick={() => setIsInfoModalOpen(false)}>CERRAR FICHA</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={isBranchesModalOpen} onClose={() => setIsBranchesModalOpen(false)} title={`Asignar Sucursales: ${selectedStaffForBranches?.full_name || selectedStaffForBranches?.name || ''}`}>
        {selectedStaffForBranches && (
          <div className="branches-assignment-modal">
            <p className="modal-description-p">Selecciona las sedes donde atiende este profesional:</p>
            <div className="branches-checkbox-list">
              {branchesList.map(branch => (
                <div 
                  key={branch.id} 
                  className={`branch-checkbox-card ${selectedBranches.includes(branch.id) ? 'checked' : ''}`}
                  onClick={() => handleToggleBranchCheckbox(branch.id)}
                >
                  <div className="branch-card-selection">
                    <input 
                      type="checkbox" 
                      checked={selectedBranches.includes(branch.id)}
                      onChange={() => {}}
                    />
                    <div className="branch-card-details">
                      <span className="branch-name-span">{branch.name}</span>
                      <span className="branch-address-span">{branch.address}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="form-footer-p" style={{ marginTop: '2rem' }}>
              <button className="btn-cancel-modal" onClick={() => setIsBranchesModalOpen(false)}>CANCELAR</button>
              <button className="btn-save-p" onClick={handleSaveBranches} disabled={savingBranches}>
                {savingBranches ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                GUARDAR ASIGNACIONES
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={isUsersModalOpen} onClose={() => setIsUsersModalOpen(false)} title={`Gestionar Usuarios: ${selectedStaffForUsers?.full_name || selectedStaffForUsers?.name || ''}`}>
        {selectedStaffForUsers && (
          <div className="users-management-modal">
            <p className="modal-description-p">Administra los usuarios autorizados para acceder a esta área del staff.</p>
            
            {/* Add User Mini Form */}
            <form onSubmit={handleAddUser} className="add-user-mini-form">
              <h4>Agregar Nuevo Usuario</h4>
              <div className="mini-form-grid">
                <div className="form-group-mini">
                  <label>Nombre Visible</label>
                  <input 
                    type="text" 
                    placeholder="Ej: OPERADOR 01" 
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                  />
                </div>
                <div className="form-group-mini">
                  <label>Nombre de Usuario</label>
                  <input 
                    type="text" 
                    placeholder="Ej: drrossi" 
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                  />
                </div>
                <div className="form-group-mini">
                  <label>Contraseña</label>
                  <input 
                    type="text" 
                    placeholder="Contraseña" 
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className="form-group-mini">
                  <label>Rol</label>
                  <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)}>
                    <option value="user">Usuario</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <button type="submit" className="btn-add-user-mini">
                  <Plus size={16} /> AGREGAR
                </button>
              </div>
            </form>

            <div className="users-list-wrapper">
              <h4>Usuarios con Acceso ({tempUsersList.length})</h4>
              {tempUsersList.length === 0 ? (
                <p className="no-users-text">No hay usuarios asignados a este integrante del staff. Agrega uno arriba.</p>
              ) : (
                <div className="users-mini-table-wrapper">
                  <table className="users-mini-table">
                    <thead>
                      <tr>
                        <th>Nombre Visible</th>
                        <th>Nombre de Usuario</th>
                        <th>Contraseña</th>
                        <th>Rol</th>
                        <th className="text-center">Eliminar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tempUsersList.map((user, idx) => (
                        <tr key={idx}>
                          <td>
                            <input 
                              type="text" 
                              value={user.display_name || ''} 
                              onChange={(e) => handleUpdateUserField(idx, 'display_name', e.target.value)} 
                              className="user-inline-input"
                              placeholder="Firma"
                            />
                          </td>
                          <td>
                            <input 
                              type="text" 
                              value={user.username} 
                              onChange={(e) => handleUpdateUserField(idx, 'username', e.target.value)} 
                              className="user-inline-input"
                              required
                            />
                          </td>
                          <td>
                            <input 
                              type="text" 
                              value={user.password} 
                              onChange={(e) => handleUpdateUserField(idx, 'password', e.target.value)} 
                              className="user-inline-input"
                              required
                            />
                          </td>
                          <td>
                            <select 
                              value={user.role} 
                              onChange={(e) => handleUpdateUserField(idx, 'role', e.target.value)}
                              className="user-inline-select"
                            >
                              <option value="user">Usuario</option>
                              <option value="admin">Administrador</option>
                            </select>
                          </td>
                          <td className="text-center">
                            <button type="button" className="btn-user-delete-mini" onClick={() => handleDeleteUser(idx)}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="form-footer-p" style={{ marginTop: '2rem' }}>
              <button className="btn-cancel-modal" onClick={() => setIsUsersModalOpen(false)}>CANCELAR</button>
              <button className="btn-save-p" onClick={handleSaveUsers} disabled={savingUsers}>
                {savingUsers ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                GUARDAR CAMBIOS
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Staff;
